import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import Card from '@amzn/meridian/card';
import Button from '@amzn/meridian/button';
import Column from '@amzn/meridian/column';
import Image from '@amzn/meridian/image';
import Link from '@amzn/meridian/link';
import Popover from '@amzn/meridian/popover';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import RadioButton from '@amzn/meridian/radio-button';
import Box from '@amzn/meridian/box';
import Modal from '@amzn/meridian/modal';
import type {
  AssetDto,
  CampaignDesiredStateDto,
  ThumbnailAssetDto,
} from 'src/store/generated/leela-api-generated';
import { DateTime } from 'luxon';
import { sanitizeMeridianImageSrc } from 'src/utils';
import { BenderAssetType, FEATURE_FLAGS } from 'src/constants';
import { isFeatureEnabled } from 'src/feature-flags';
import Icon from '@amzn/meridian/icon';
import { useTranslation } from 'src/arb-wrapper/useTranslation';
import editTokens from '@amzn/meridian-tokens/base/icon/edit';
import { useAssetsControllerUpdateMutation } from 'src/store/leela-api';
import { useToasterContext } from 'src/components/common/alerts/ToasterContext';
import { AlertType } from 'src/components/common/alerts/alert.types';
import { Logger } from 'src/utils/logger';
import documentTokens from '@amzn/meridian-tokens/base/icon/document';
import folderTokens from '@amzn/meridian-tokens/base/icon/folder';
import copyTokens from '@amzn/meridian-tokens/base/icon/copy';
import globeTokens from '@amzn/meridian-tokens/base/icon/globe';
import errorTokens from '@amzn/meridian-tokens/base/icon/error';
import calendarTokens from '@amzn/meridian-tokens/base/icon/calendar';
import clockTokens from '@amzn/meridian-tokens/base/icon/clock';
import closeCircleTokens from '@amzn/meridian-tokens/base/icon/close-circle';
import alertTokens from '@amzn/meridian-tokens/base/icon/alert';
import signalTokens from '@amzn/meridian-tokens/base/icon/signal-100';
import Divider from '@amzn/meridian/divider';
import { extractFilenameFromAsset } from 'src/utils/AssetUtils';
import Tooltip from '@amzn/meridian/tooltip';
import { AssetEvalBadge } from './AssetEvalBadge';
import { AssetSourceTag } from './AssetSourceTag';
import type { RuleMap } from 'src/types/assetEval';
import { ScheduleInputGroup } from './ScheduleInputGroup';
import { formatScheduleGapRanges, type ScheduleWarning } from 'src/utils/computeScheduleWarnings';
import { formatUtcDate, formatUtcDateTime } from 'src/utils/formatDateTime';

import { RELEASE_DATE_FALLBACK, ReleaseDateSkeleton } from './release-date-display';

function getReleaseDateDisplayValues (
  titles: AssetDto['titles'],
  releaseDatesByGti: ReadonlyMap<string, string | undefined> | undefined,
): string[] {
  if (titles.length === 0) return [RELEASE_DATE_FALLBACK];
  if (titles.length === 1) {
    return [formatUtcDate(releaseDatesByGti?.get(titles[0].gti), RELEASE_DATE_FALLBACK)];
  }
  return titles.map(({ gti, name }) =>
    `${name ?? RELEASE_DATE_FALLBACK} — ${formatUtcDate(releaseDatesByGti?.get(gti), RELEASE_DATE_FALLBACK)}`,
  );
}

const SCHEDULE_WARNING_I18N_KEY: Record<ScheduleWarning['type'], string> = {
  OVERLAP: 'asset_schedule_warning_overlap',
  PAST_EVENT_END: 'asset_schedule_warning_past_event_end',
  COVERAGE_GAP: 'asset_schedule_warning_coverage_gap',
};

export interface AssetCardProps {
  asset: AssetDto;
  programId: string;
  campaigns: CampaignDesiredStateDto[];
  isSelected: boolean;
  isReleaseDatesFetching?: boolean;
  onSelect: (asset: AssetDto) => void;
  releaseDatesByGti?: ReadonlyMap<string, string | undefined>;
  schedule?: { startTime: string; endTime: string };
  warnings?: ScheduleWarning[];
  onScheduleChange?: (schedule: { startTime: string; endTime: string }) => void;
  // Display name of the asset's product strategy (empty for legacy strategy-less rows).
  strategyLabel?: string;
  // Rule metadata for resolving the evaluation badge; when absent the badge is not rendered.
  ruleMap?: RuleMap;
}

export const AssetCard = (props: AssetCardProps) => {
  const {
    asset,
    campaigns,
    isReleaseDatesFetching,
    programId,
    releaseDatesByGti,
    schedule,
    warnings,
    onScheduleChange,
    strategyLabel,
    ruleMap,
  } = props;
  const {
    assetId,
    destinationUrl,
    campaignIds,
    destinationUrls,
    titles,
  } = asset;

  const { t } = useTranslation();
  const isReleaseDateEnabled = isFeatureEnabled(
    FEATURE_FLAGS.ENABLE_ASSET_IMPROVEMENTS,
  );
  const [isAssetPreviewModalOpen, setIsAssetPreviewModalOpen] = useState(false);
  const [isScheduleModalOpen, setIsScheduleModalOpen] = useState(false);

  const titleNames = useMemo(
    () => titles.map((title) => title.name).join(', '),
    [titles],
  );
  const releaseDateDisplayValues = getReleaseDateDisplayValues(titles, releaseDatesByGti);

  const [popoverIsVisible, setPopoverIsVisible] = useState(false);

  const popoverUrls = useMemo(
    () => {
      const urls = destinationUrls?.length > 0
        ? destinationUrls
        : destinationUrl ? [{ url: destinationUrl, campaignId: 'default' }] : [];
      const seen = new Set<string>();
      return urls.filter((d) => {
        const key = `${d.campaignId}::${d.url}`;
        if (!d.url || seen.has(key)) return false;
        seen.add(key);
        return true;
      });
    },
    [destinationUrl, destinationUrls],
  );

  const assetFilename = useMemo(() => extractFilenameFromAsset(asset, t), [asset, t]);
  const popoverButtonRef = useRef();

  const showPopover = () => {
    setPopoverIsVisible(true);
  };

  const hidePopover = () => {
    setPopoverIsVisible(false);
  };

  const [strategies, countries] = useMemo(() => {
    const _strategies = new Set();
    const _countries = new Set();

    for (const campaign of campaigns) {
      if (!campaignIds.some(id => String(id) === String(campaign.campaignId))) {
        continue;
      }

      campaign.productStrategies.forEach((strategy) => {
        if (strategy.strategyId) {
          _strategies.add(strategy.strategyId);
        }
        if (strategy.pageId) {
          _strategies.add(strategy.pageId);
        }
        _countries.add(`${strategy.country} (${strategy.locales.join(', ')})`);
      });
    };

    return [
      Array.from(_strategies).join(', '),
      Array.from(_countries).join(', '),
    ];
  }, [campaigns, campaignIds]);

  return (
  <>
    <Card data-testid={`asset-card-${assetId}`} spacingInset="none" width={280}>
      <Row
        alignmentHorizontal="justify"
        alignmentVertical="center"
        backgroundColor="highlight"
        maxWidth="100%"
        spacingInset="300"
      >
        <Column overflowX="hidden">
          <>
            <Button
              aria-expanded="false"
              aria-haspopup="dialog"
              data-testid="destination-urls-popover-btn"
              propagateClickEvent={false}
              ref={popoverButtonRef}
              type="link"
              onClick={showPopover}
            >
              <Text truncate={true} type="h100">
                {titleNames}
              </Text>
            </Button>
            <Popover
              anchorNode={popoverButtonRef.current}
              open={popoverIsVisible}
              position="bottom"
              onClose={hidePopover}
            >
              <Column>
                {popoverUrls.map((destinationUrl) => (
                  <Link
                    data-testid={`asset-destination-link-${assetId}-${destinationUrl.campaignId}`}
                    href={destinationUrl.url}
                    key={destinationUrl.campaignId}
                    target="_blank"
                    type="secondary"
                  >
                    <span style={{ color: '#333' }}>{destinationUrl.url}</span>
                  </Link>
                ))}
              </Column>
            </Popover>
          </>
        </Column>
        <Row spacing="200">
          <AssetSourceTag
            assetId={assetId}
            generationMethods={asset.metadata?.generationMethods}
            releaseDates={titles.map(({ gti }) => releaseDatesByGti?.get(gti))}
          />
          {shouldShowAdspStvWarning(asset) && (
            <Tooltip id="adsp-video-message" position="end" title={t('adsp_video_stv_warning')}>
              <Icon color="error" data-testid="adsp-stv-warning-icon" tokens={errorTokens} />
            </Tooltip>
          )}
          {ruleMap && (
            <AssetEvalBadge
              assetId={assetId}
              evaluatedRules={asset.evaluatedRules}
              ruleMap={ruleMap}
            />
          )}
        </Row>
      </Row>
      <Column alignmentVertical="center" height={265} spacingInset="200">
          <AssetDisplay
            asset={asset}
            programId={programId}
            onImageClick={() => { setIsAssetPreviewModalOpen(true); }}
          />
        </Column>
      <Column spacingInset="200">
        <>
          <Box spacingInset="200 200 300 300">
            <RadioButton
              checked={props.isSelected}
              data-testid={`asset-approve-radio-button-${assetId}`}
              size="large"
              onChange={() => {
                props.onSelect(asset);
              }}
            >
              {t('asset_card_radio_button_approve')}
            </RadioButton>
          </Box>
          <Box spacingInset="200 200 300 300">
            <RadioButton
              checked={!props.isSelected}
              data-testid={`asset-reject-radio-button-${assetId}`}
              size="large"
              onChange={() => {
                props.onSelect(asset);
              }}
            >
              {t('asset_card_radio_button_reject')}
            </RadioButton>
          </Box>
        </>
      </Column>
        <Divider size="small" />

      <Row
        alignmentHorizontal="justify"
        alignmentVertical="top"
        spacingInset="300"
        >
          <Column spacing="200">
            {shouldShowAdspStvWarning(asset) && (
              <Row
                alignmentHorizontal="start"
                alignmentVertical="center"
                spacing="300"
              >
                <Icon color="error" tokens={closeCircleTokens} />
                <Text data-testid={`asset-stv-invalid-${assetId}`} type="b200">
                  {t('adsp_stv_invalid_label')}
                </Text>
              </Row>
            )}
            {strategies && (
              <Row
                alignmentHorizontal="start"
                alignmentVertical="center"
                spacing="300"
              >
                <Icon tokens={folderTokens} />
                <div
                  style={{
                    wordBreak: 'break-word',
                    overflowWrap: 'break-word',
                    maxWidth: '240px',
                    display: 'block',
                  }}
                >
                  <Text
                    breakWord={true}
                    className="asset-strategies-container"
                    color="secondary"
                    data-testid={`asset-strategies-${assetId}`}
                    type="b200"
                  >
                    {strategies}
                  </Text>
                </div>
              </Row>
            )}
            <Row
              alignmentHorizontal="start"
              alignmentVertical="center"
              spacing="300"
            >
              <Icon tokens={documentTokens} />
              <div
                style={{
                  wordBreak: 'break-word',
                  overflowWrap: 'break-word',
                  maxWidth: '240px',
                  display: 'block',
                }}
              >
                <Text
                  breakWord={true}
                  className="asset-filename-container"
                  color="secondary"
                  data-testid={`asset-filename-${assetId}`}
                  type="b200"
                >
                  {assetFilename}
                </Text>
              </div>
            </Row>
            {isReleaseDateEnabled && (
              <Row
                alignmentHorizontal="start"
                alignmentVertical="center"
                data-testid={`asset-release-date-${assetId}`}
                spacing="300"
              >
                <Icon tokens={clockTokens} />
                <div
                  style={{
                    wordBreak: 'break-word',
                    overflowWrap: 'break-word',
                    maxWidth: '240px',
                    display: 'block',
                    width: '100%',
                  }}
                >
                  <Row
                    alignmentHorizontal="start"
                    alignmentVertical="center"
                    data-testid={`asset-release-date-line-${assetId}`}
                    spacing="100"
                    wrap="down"
                  >
                    <Text color="secondary" type="b200">
                      {t('asset_card_release_date')}
                    </Text>
                    {isReleaseDatesFetching
                      ? <ReleaseDateSkeleton testId={`asset-release-date-skeleton-${assetId}`} />
                      : releaseDateDisplayValues.map((releaseDate, index) => (
                        <Text
                          color="secondary"
                          data-testid={`asset-release-date-value-${assetId}`}
                          key={`${releaseDate}-${index}`}
                          type="b200"
                        >
                          {releaseDate}
                        </Text>
                      ))}
                  </Row>
                </div>
              </Row>
            )}
            {countries
              ? (
              <Row
                alignmentHorizontal="start"
                alignmentVertical="center"
                spacing="300"
              >
                <Icon tokens={globeTokens} />
                <Text
                  color="secondary"
                  data-testid={`asset-countries-${assetId}`}
                  type="b200"
                >
                  {countries}
                </Text>
              </Row>
                )
              : asset.locale
                ? (
              <Row
                alignmentHorizontal="start"
                alignmentVertical="center"
                spacing="300"
              >
                <Icon tokens={globeTokens} />
                <Text
                  color="secondary"
                  data-testid={`asset-locale-${assetId}`}
                  type="b200"
                >
                  {asset.locale}
                </Text>
              </Row>
                  )
                : null}
            {strategyLabel && (
              <Row
                alignmentHorizontal="start"
                alignmentVertical="center"
                spacing="300"
              >
                <Icon tokens={copyTokens} />
                <div
                  style={{
                    wordBreak: 'break-word',
                    overflowWrap: 'break-word',
                    maxWidth: '240px',
                    display: 'block',
                  }}
                >
                  <Text
                    breakWord={true}
                    color="secondary"
                    data-testid={`asset-strategy-${assetId}`}
                    type="b200"
                  >
                    {t('asset_strategy_name', { name: strategyLabel })}
                  </Text>
                </div>
              </Row>
            )}
            {asset.schedule && !onScheduleChange && (
              <Column spacing="100">
                <Text color="secondary" data-testid={`asset-schedule-start-${assetId}`} type="b200">
                  {t('asset_schedule_goes_live', { time: formatUtcDateTime(asset.schedule.startTime, 'Invalid DateTime') })}
                </Text>
                <Text color="secondary" data-testid={`asset-schedule-end-${assetId}`} type="b200">
                  {t('asset_schedule_taken_down', { time: formatUtcDateTime(asset.schedule.endTime, 'Invalid DateTime') })}
                </Text>
              </Column>
            )}
            {onScheduleChange && (
              <Row
                alignmentHorizontal="start"
                alignmentVertical="center"
                spacing="300"
              >
                <Icon tokens={calendarTokens} />
                {(() => {
                  const resolvedStart = schedule?.startTime ?? asset.schedule?.startTime;
                  const resolvedEnd = schedule?.endTime ?? asset.schedule?.endTime;
                  const openScheduleModal = () => { setIsScheduleModalOpen(true); };
                  // With no schedule yet, the "+ Set schedule" text is itself the affordance;
                  // once set, the schedule reads as plain text with a separate "(Edit)" link.
                  return resolvedStart && resolvedEnd
                    ? (
                    // alignmentHorizontal="start" keeps the "(Edit)" link's hit area at text
                    // width instead of stretching across the card.
                    <Column alignmentHorizontal="start" spacing="none">
                      <Text color="secondary" type="b200">
                        {t('asset_schedule_goes_live', { time: DateTime.fromISO(resolvedStart, { zone: 'utc' }).toFormat('MMM dd, HH:mm') })}
                      </Text>
                      <Text color="secondary" type="b200">
                        {t('asset_schedule_taken_down', { time: DateTime.fromISO(resolvedEnd, { zone: 'utc' }).toFormat('MMM dd, HH:mm') })}
                      </Text>
                      <Link
                        data-testid={`asset-schedule-edit-${assetId}`}
                        type="secondary"
                        onClick={openScheduleModal}
                      >
                        <Text color="secondary" type="b200">{t('asset_schedule_edit')}</Text>
                      </Link>
                    </Column>
                      )
                    : (
                    <Link
                      data-testid={`asset-schedule-edit-${assetId}`}
                      type="secondary"
                      onClick={openScheduleModal}
                    >
                      <Text color="secondary" type="b200">{t('asset_schedule_set')}</Text>
                    </Link>
                      );
                })()}
              </Row>
            )}
            {asset.metadata?.liveStatus && (
              <Row
                alignmentHorizontal="start"
                alignmentVertical="center"
                spacing="300"
              >
                <Icon tokens={signalTokens} />
                <Text color="secondary" data-testid={`asset-live-status-${assetId}`} type="b200">
                  {t('asset_live_status', { status: asset.metadata.liveStatus })}
                </Text>
              </Row>
            )}
            {warnings?.map((warning) => (
              <Row
                alignmentHorizontal="start"
                alignmentVertical="center"
                key={warning.type}
                spacing="300"
              >
                <Icon color="warning" tokens={alertTokens} />
                <Text
                  data-testid={`asset-schedule-warning-${warning.type}-${assetId}`}
                  type="b200"
                >
                  {warning.type === 'COVERAGE_GAP' && warning.gaps?.length
                    ? t('asset_schedule_warning_coverage_gap_detail_card', {
                      ranges: formatScheduleGapRanges(warning.gaps),
                    })
                    : t(SCHEDULE_WARNING_I18N_KEY[warning.type])}
                </Text>
              </Row>
            ))}
          </Column>
        </Row>
    </Card>

      {/* Asset Preview Modal */}
      <Modal
        data-testid="asset-preview-modal"
        open={isAssetPreviewModalOpen}
        width={800}
        onClose={() => { setIsAssetPreviewModalOpen(false); }}
      >
        <Column spacing="300">
          <Text type="h300">
            {t('asset_detail_asset_preview_title')}
          </Text>
          <Text color="secondary" type="b300">
            {t('asset_detail_preview_sub_title', { assetId })}
          </Text>
          {asset.assetType === BenderAssetType.VIDEO && (
            <Column spacing="200">
              {asset?.metadata?.videoBitrate && (
                <Text color="secondary" type="b200">
                  Video Bitrate: {asset?.metadata?.videoBitrate} kbps
                </Text>
              )}
              {asset?.metadata?.audioBitrate && (
                <Text color="secondary" type="b200">
                  Audio Bitrate: {asset?.metadata?.audioBitrate} kbps
                </Text>
              )}
            </Column>
          )}
          <Box backgroundColor="#E9EBEF" spacingInset="400">
            <AssetDisplay asset={asset} programId={programId} onImageClick={undefined} />
          </Box>
          <Row alignmentHorizontal="end" spacing="300">
            <Button
              data-testid="asset-preview-modal-close-button"
              type="primary"
              onClick={() => { setIsAssetPreviewModalOpen(false); }}
            >
              {t('asset_card_select_thumbnail_modal_cancel')}
            </Button>
          </Row>
        </Column>
      </Modal>

      {/* Schedule Edit Modal */}
      {onScheduleChange && (
        <Modal
          data-testid={`asset-schedule-modal-${assetId}`}
          open={isScheduleModalOpen}
          title={t('asset_detail_schedule_title')}
          width={500}
          onClose={() => { setIsScheduleModalOpen(false); }}
        >
          <ScheduleInputGroup
            endTime={schedule?.endTime ?? asset.schedule?.endTime ?? ''}
            startTime={schedule?.startTime ?? asset.schedule?.startTime ?? ''}
            onEndTimeChange={(endTime) => { onScheduleChange({ startTime: schedule?.startTime ?? asset.schedule?.startTime ?? '', endTime }); }}
            onStartTimeChange={(startTime) => { onScheduleChange({ startTime, endTime: schedule?.endTime ?? asset.schedule?.endTime ?? '' }); }}
          />
        </Modal>
      )}
    </>
  );
};

export interface AssetDisplayProps {
  asset: AssetDto;
  programId: string;
  onImageClick?: () => void;
}

export const AssetDisplay = (props: AssetDisplayProps) => {
  const { asset, programId, onImageClick } = props;
  const { assetId, assetType, assetUrl, thumbnailOptions } = asset;
  const hasThumbnailOptions = asset.thumbnailOptions && asset.thumbnailOptions.length > 0;
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedThumbnailOptions, setSelectedThumbnails] =
    useState(thumbnailOptions);
  const { t } = useTranslation();
  const [updateAssets] = useAssetsControllerUpdateMutation();
  const { createToast } = useToasterContext();
  const [isLoading, setIsLoading] = useState(false);
  const [selectedThumbnailIndex, setSelectedThumbnailIndex] = useState(0);

  const handleSingleUpdateThumbnail = useCallback(
    async (
      asset: AssetDto,
      programId: string,
      thumbnailOptions: ThumbnailAssetDto[] | undefined,
    ): Promise<void> => {
      try {
        await updateAssets({
          updateAssetsDto: {
            assets: [
              {
                assetId: asset.assetId,
                programId,
                approvalStatus: asset.approvalStatus,
                thumbnailOptions,
                // Scope to this row's strategy so the update doesn't clobber sibling strategies.
                ...(asset.productStrategyId ? { productStrategyId: asset.productStrategyId } : {}),
              },
            ],
          },
        });
        createToast({
          type: AlertType.SUCCESS,
          message: t('asset_thumbnail_update_success'),
        });
      } catch (e) {
        createToast({
          type: AlertType.ERROR,
          message: t('asset_update_failure'),
        });

        const error = e as Error;
        Logger.error('Error updating asset approval status', {
          asset,
          error,
        });
      }
    },
    [updateAssets, createToast, t],
  );

  const getThumbnailIndexFromOptions = useCallback(() => {
    const approvedThumbnailIndex = selectedThumbnailOptions?.findIndex(
      (option) => {
        return option.approvalStatus === 'APPROVED';
      },
    );

    if (approvedThumbnailIndex !== undefined && approvedThumbnailIndex !== -1) {
      return approvedThumbnailIndex;
    } else {
      return 0;
    }
  }, [selectedThumbnailOptions]);

  const getThumbnailFromOptions = useCallback(() => {
    const approvedThumbnail =
      selectedThumbnailOptions?.[getThumbnailIndexFromOptions()];

    if (approvedThumbnail) {
      return approvedThumbnail.assetUrl;
    }
    return '';
  }, [selectedThumbnailOptions, getThumbnailIndexFromOptions]);

  const handleThumbnailApproval = async () => {
    setIsLoading(true);
    const updatedOptions: ThumbnailAssetDto[] | undefined =
      selectedThumbnailOptions?.map((option, i) => {
        if (i === selectedThumbnailIndex) {
          return { ...option, approvalStatus: 'APPROVED' };
        }
        return { ...option, approvalStatus: 'PENDING' };
      });
    if (!updatedOptions) {
      return;
    }
    // Update frontend UI with new thumbnail options
    setSelectedThumbnails(updatedOptions);
    // Update backend with new thumbnail options
    await handleSingleUpdateThumbnail(asset, programId, updatedOptions);

    setIsLoading(false);
    closeThumbnailModal();
  };

  const handleThumbnailSelect = (index: number) => {
    setSelectedThumbnailIndex(index);
  };

  const openThumbnailModal = () => {
    setIsModalOpen(true);
  };

  const closeThumbnailModal = () => {
    setIsModalOpen(false);
  };

  useEffect(() => {
    setSelectedThumbnailIndex(getThumbnailIndexFromOptions());
  }, [selectedThumbnailOptions, getThumbnailIndexFromOptions]);

  return (
    <>
      {assetType === BenderAssetType.VIDEO
        ? (
        <Row
          alignmentHorizontal="center"
          alignmentVertical="center"
          height="100%"
          maxWidth="100%"
          spacing="200"
          spacingInset="300"
          widths={hasThumbnailOptions ? ['fill', 'fill'] : ['fill']}
        >
          <Column alignmentVertical="center" height="100%">
            <div
              className="asset-detail-card-video"
              style={hasThumbnailOptions
                ? {}
                : {
                    maxHeight: '200px',
                    width: '100%',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
            >
              <video
                controls
                data-testid={`asset-video-link-${assetId}`}
                preload="none"
                src={sanitizeMeridianImageSrc(assetUrl)}
                style={hasThumbnailOptions
                  ? {}
                  : {
                      maxWidth: '100%',
                      maxHeight: '100%',
                      objectFit: 'contain',
                    }}
              />
            </div>
            <Text alignment="center" color="secondary" type="b200">
              {t('asset_card_video_header')}
            </Text>
          </Column>
          {hasThumbnailOptions && (
            <Column>
              {thumbnailOptions!.length > 1
                ? (
                <div style={{ position: 'relative' }}>
                  <Image
                    data-testid={`asset-thumbnail-link-${assetId}`}
                    size="contain"
                    src={sanitizeMeridianImageSrc(getThumbnailFromOptions())}
                    viewportAspectRatio={9 / 16}
                  />
                  <div
                    style={{
                      position: 'absolute',
                      top: '-8px',
                      right: '-8px',
                    }}
                  >
                    <Button
                      circular={true}
                      data-testid={`thumbnail-select-button-${assetId}`}
                      size="small"
                      type="primary"
                      onClick={openThumbnailModal}
                    >
                      <Icon color="inverted" tokens={editTokens} />
                    </Button>
                  </div>
                </div>
                  )
                : (
                <Image
                  data-testid={`asset-thumbnail-link-${assetId}`}
                  size="contain"
                  src={sanitizeMeridianImageSrc(getThumbnailFromOptions())}
                  viewportAspectRatio={9 / 16}
                />
                  )}
              <Text alignment="center" color="secondary" type="b200">
                {t('asset_card_thumbnail_header')}
              </Text>
            </Column>
          )}
        </Row>
          )
        : (
        <Link
          onClick={onImageClick}
        >
          <Image
            data-testid={`asset-image-link-${assetId}`}
            size="contain"
            src={sanitizeMeridianImageSrc(assetUrl)}
            viewportAspectRatio={1}
          />
        </Link>
          )}
        <Modal
          data-testid="thumbnail-selection-modal"
          open={isModalOpen}
          onClose={closeThumbnailModal}
        >
          <Column spacing="300">
            <Text type="h300">
              {t('asset_card_select_thumbnail_modal_description')}
            </Text>
            <Row spacing="300" wrap="up">
              {thumbnailOptions?.map((option, index) => (
                <Column key={index} spacing="200" width={200}>
                  <Card
                    aria-checked={index === selectedThumbnailIndex}
                    checked={index === selectedThumbnailIndex}
                    data-testid={`thumbnail-option-${index}`}
                    onClick={() => {
                      handleThumbnailSelect(index);
                    }}
                  >
                    <Image
                      size="contain"
                      src={sanitizeMeridianImageSrc(option.assetUrl)}
                      viewportAspectRatio={9 / 16}
                    />
                  </Card>
                </Column>
              ))}
            </Row>
            <Row alignmentHorizontal="end" spacing="300">
              <Button
                data-testid="thumbnail-selection-modal-cancel-button"
                disabled={isLoading}
                type="secondary"
                onClick={() => {
                  setSelectedThumbnailIndex(getThumbnailIndexFromOptions());
                  closeThumbnailModal();
                }}
              >
                {t('asset_card_select_thumbnail_modal_cancel')}
              </Button>
              <Button
                data-testid="thumbnail-selection-modal-confirm-button"
                disabled={isLoading}
                type="primary"
                onClick={() => {
                  handleThumbnailApproval();
                }}
              >
                {t('asset_card_select_thumbnail_modal_confirm')}
              </Button>
            </Row>
          </Column>
        </Modal>

    </>
  );
};

export function shouldShowAdspStvWarning (asset: AssetDto) {
  const MIN_AUDIO_BITRATE_ADSP = 192000;
  const MIN_VIDEO_BITRATE_ADSP = 4000000;

  if (asset.assetType !== 'VIDEO') {
    return false;
  }

  const hasLowVideoBitrate = !!asset.metadata?.videoBitrate && asset.metadata.videoBitrate < MIN_VIDEO_BITRATE_ADSP;
  const hasLowAudioBitrate = !!asset.metadata?.audioBitrate && asset.metadata.audioBitrate < MIN_AUDIO_BITRATE_ADSP;

  return hasLowVideoBitrate || hasLowAudioBitrate;
}
